<template>
  <div v-if="total > 0" class="import-progress-bar">
    <div class="import-progress-header">
      <small class="import-progress-label">{{ $t('message.import_progress_label') }}</small>
      <small class="import-progress-count">{{ completed + failed }} / {{ total }}</small>
    </div>
    <div class="import-progress-track">
      <div
        v-if="segmentCompleted > 0"
        class="import-progress-segment segment-completed"
        role="progressbar"
        :style="{ width: segmentCompleted + '%' }"
        :aria-valuenow="completed"
        aria-valuemin="0"
        :aria-valuemax="total"
      />
      <div
        v-if="segmentFailed > 0"
        class="import-progress-segment segment-failed"
        role="progressbar"
        :style="{ width: segmentFailed + '%' }"
        :aria-valuenow="failed"
        aria-valuemin="0"
        :aria-valuemax="total"
      />
      <div
        v-if="segmentEnqueued > 0"
        class="import-progress-segment segment-enqueued"
        role="progressbar"
        :style="{ width: segmentEnqueued + '%' }"
        :aria-valuenow="enqueued"
        aria-valuemin="0"
        :aria-valuemax="total"
      />
      <div
        v-if="segmentWaiting > 0"
        class="import-progress-segment segment-waiting"
        role="progressbar"
        :style="{ width: segmentWaiting + '%' }"
        :aria-valuenow="waiting"
        aria-valuemin="0"
        :aria-valuemax="total"
      />
    </div>
    <div class="import-progress-legend">
      <span v-if="completed > 0" class="legend-item">
        <span class="legend-badge completed">{{ completed }}</span>
        {{ $t('message.import_progress_completed') }}
      </span>
      <span v-if="failed > 0" class="legend-item">
        <span class="legend-badge failed">{{ failed }}</span>
        {{ $t('message.import_progress_failed') }}
      </span>
      <span v-if="enqueued > 0" class="legend-item">
        <span class="legend-badge enqueued">{{ enqueued }}</span>
        {{ $t('message.import_progress_enqueued') }}
      </span>
      <span v-if="waiting > 0" class="legend-item">
        <span class="legend-badge waiting">{{ waiting }}</span>
        {{ $t('message.import_progress_waiting') }}
      </span>
    </div>
  </div>
</template>

<script>
/**
 * Progress bar for domain import. Segments: completed (has result), failed (has errors),
 * enqueued (in-flight), waiting (pending). Completed/failed can overlap with enqueued
 * until the next render cycle after the WS response.
 */
export default {
  name: 'ImportProgressBar',
  props: {
    total: { type: Number, required: true },
    completed: { type: Number, default: 0 },
    failed: { type: Number, default: 0 },
    enqueued: { type: Number, default: 0 },
    waiting: { type: Number, default: 0 },
  },
  computed: {
    segmentCompleted() {
      return this.total ? (this.completed / this.total) * 100 : 0;
    },
    segmentFailed() {
      return this.total ? (this.failed / this.total) * 100 : 0;
    },
    segmentEnqueued() {
      return this.total ? (this.enqueued / this.total) * 100 : 0;
    },
    segmentWaiting() {
      return this.total ? (this.waiting / this.total) * 100 : 0;
    },
  },
};
</script>

<style scoped>
.import-progress-bar {
  margin-bottom: 1rem;
}

.import-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.import-progress-label,
.import-progress-count {
  font-size: 0.8125rem;
  color: var(--muted-color, #6c757d);
  font-weight: 500;
}

.import-progress-track {
  display: flex;
  height: 0.5rem;
  background: linear-gradient(90deg, #e9ecef 0%, #f1f3f5 100%);
  border-radius: 999px;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
}

.import-progress-segment {
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 0;
}

.import-progress-segment + .import-progress-segment {
  border-left: 1px solid rgba(255, 255, 255, 0.3);
}

.segment-completed {
  background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 0 12px rgba(34, 197, 94, 0.25);
}

.segment-failed {
  background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 0 12px rgba(239, 68, 68, 0.2);
}

.segment-enqueued {
  background: linear-gradient(
    90deg,
    #3b82f6 0%,
    #60a5fa 25%,
    #93c5fd 50%,
    #60a5fa 75%,
    #3b82f6 100%
  );
  background-size: 200% 100%;
  animation: gradient-shift 2s ease-in-out infinite;
  box-shadow: 0 0 12px rgba(59, 130, 246, 0.3);
}

.segment-waiting {
  background: linear-gradient(90deg, #94a3b8 0%, #64748b 100%);
}

@keyframes gradient-shift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.import-progress-legend {
  display: flex;
  flex-wrap: wrap;
  margin-top: 0.625rem;
  gap: 0.75rem;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--text-secondary, #64748b);
}

.legend-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.25rem;
  padding: 0 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 999px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.legend-badge:hover {
  transform: scale(1.05);
}

.legend-badge.completed {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  box-shadow: 0 1px 3px rgba(34, 197, 94, 0.3);
}

.legend-badge.failed {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 1px 3px rgba(239, 68, 68, 0.3);
}

.legend-badge.enqueued {
  background: linear-gradient(
    135deg,
    #3b82f6 0%,
    #60a5fa 50%,
    #3b82f6 100%
  );
  background-size: 200% 200%;
  color: white;
  box-shadow: 0 1px 3px rgba(59, 130, 246, 0.3);
  animation: badge-pulse 2s ease-in-out infinite;
}

.legend-badge.waiting {
  background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
  color: white;
  box-shadow: 0 1px 3px rgba(100, 116, 139, 0.2);
}

@keyframes badge-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.85;
  }
}
</style>
